import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CSSTransition } from 'react-transition-group';
import { primaryHeaderHeight } from '../../../header.utils';
import { MenuLink, MenuLinkDataProps } from './MenuLink';
import { EditionDataProps, getActiveEdition } from '../../Editions';
import {
  linkEnterDelay,
  linkEnterDuration, linkEnterTimeOut, linkExitDelay, linkExitDuration, linkExitTimeOut,
  menuEnterDuration,
  menuEnterTimeOut,
  menuExitDelay,
  menuExitDuration,
  menuExitTimeOut,
} from './menuLinks.utils';
import { useTheme } from '../../../../../../../theming/useTheme';
import { ErrorBoundary } from '../../../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../../../mediaQueries.const';
import { BaseUnit } from '../../../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'menuLinks';

export interface MenuLinksDataProps {
  menuLinks: MenuLinkDataProps[];
  editions: EditionDataProps[] | null;
  editionEndpoint: string;
}

export interface MenuLinksThemeProps {
  backgroundColor: string;
  dividerColor: string;
  baseUnit: BaseUnit;
}

interface MenuLinksLogicProps {
  isMenuOpen: boolean;
  onClick: (index: number) => void;
  currentOpenLink: number;
}

const getMenuLinksStyle = (backgroundColor: string, dividerColor: string, baseUnit: BaseUnit) => StyleSheet.create({
  style: {
    backgroundColor,
    position: 'fixed',
    width: '100%',
    height: `calc(100vh - ${primaryHeaderHeight}px)`,
    zIndex: 2,
    top: `${primaryHeaderHeight}px`,
    left: 0,
    overflowY: 'auto',
  },
  enter: {
    opacity: 0,
    transform: 'translate(-100%)',
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${menuEnterDuration}ms, opacity ${menuEnterDuration}ms`,
    transitionTimingFunction: 'ease-out',
  },
  exit: {
    opacity: 1,
    transform: 'translate(0)',
  },
  exitActive: {
    opacity: 0,
    transform: 'translate(-100%)',
    transition: `transform ${menuExitDuration}ms ease-in , opacity ${menuExitDuration}ms ease-in`,
    transitionDelay: `${menuExitDelay}ms`,
  },
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`,
    },
  },
});

const getMenuLinkStyle = () => StyleSheet.create({
  enter: {
    opacity: 0.5,
    transform: 'translate(-100%)',

  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${linkEnterDuration}ms ease-out, opacity ${linkEnterDuration}ms ease-in`,
    transitionDelay: `${linkEnterDelay}ms`,

  },
  exit: {
    transform: 'translate(0)',
    opacity: 1,
  },
  exitActive: {
    opacity: 0.5,
    transform: 'translate(-100%)',
    transition: `transform ${linkExitDuration}ms ease-in, opacity ${linkExitDuration}ms ease-in`,
    transitionDelay: `${linkExitDelay}ms`,
  },
});

export const createMenuLink = (onClick: (index: number) => void, currentOpenLink: number, shouldAppear: boolean, startIndex = 0) => (link: MenuLinkDataProps, index: number) => {
  const style = getMenuLinkStyle();
  return (
    <CSSTransition
      key={index}
      unmountOnExit
      mountOnEnter
      in={shouldAppear}
      timeout={{
        enter: linkEnterTimeOut,
        exit: linkExitTimeOut,
      }}
      classNames={{
        enter: css(style.enter),
        enterActive: css(style.enterActive),
        exit: css(style.exit),
        exitActive: css(style.exitActive),
      }}
    >
      {(state: string) => (
        <MenuLink
          key={index}
          href={link.href}
          text={link.text}
          childrenLinks={link.childrenLinks}
          index={startIndex + index}
          onClick={onClick}
          isLinkOpen={(currentOpenLink !== -1) && (currentOpenLink === startIndex + index)}
          startChevronIconAnimation={(state === 'entering') || (state === 'entered')}
        />
      )}
    </CSSTransition>
  );
};

export const createEditionsLinks = (editions: EditionDataProps[], editionEndpoint: string) => {
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  return [{
    href: activeEdition.href,
    text: activeEdition.text,
    childrenLinks: editions.map(edition => ({
      href: edition.href,
      text: edition.text,
    })),
  }];
};

export const MenuLinks: React.FunctionComponent<MenuLinksDataProps & MenuLinksLogicProps> = props => {
  const { menuLinks, editions, isMenuOpen, onClick, currentOpenLink, editionEndpoint } = props;
  const { backgroundColor, dividerColor, baseUnit } = useTheme<MenuLinksThemeProps>(componentConfigThemeKey);
  const style = getMenuLinksStyle(backgroundColor, dividerColor, baseUnit);
  const editionsLinks = editions ? createEditionsLinks(editions, editionEndpoint) : [];
  return (
    <ErrorBoundary>
      <CSSTransition
        in={isMenuOpen}
        unmountOnExit
        mountOnEnter
        timeout={{
          enter: menuEnterTimeOut,
          exit: menuExitTimeOut,
        }}
        classNames={{
          enter: css(style.enter),
          enterActive: css(style.enterActive),
          exit: css(style.exit),
          exitActive: css(style.exitActive),
        }}
      >
        {(state: string) => (
          <ul className={css(style.style)}>
            {menuLinks.map(createMenuLink(onClick, currentOpenLink, (state === 'entering') || (state === 'entered')))}
            <div className={css(style.divider)} />
            {editionsLinks.map(createMenuLink(onClick, currentOpenLink, (state === 'entering') || (state === 'entered'), menuLinks.length))}
          </ul>
        )}
      </CSSTransition>
    </ErrorBoundary>
  );
};
